/**
 * What a CUSTOMER is allowed to see of a work order's line items.
 *
 * Canonical owner for `visibility_policy_enum` (BILLABLE_ITEMS | SUMMARY_ONLY |
 * STRUCTURED_ITEMS), which appears on both `work_orders` and `invoices`.
 *
 * The rule was previously implemented inline in
 * apps/customer-portal/src/components/Portal/QuoteDetails.js only. The logged-in portal
 * (My Jobs, Invoices) showed no items at all, so it had no opinion — but the moment those
 * screens start itemising, a second copy of this rule is a second chance to leak a
 * contractor's internal pricing to their customer. One owner instead.
 *
 * Semantics, as implemented by QuoteDetails since it shipped:
 *   BILLABLE_ITEMS    — itemised WITH per-line quantities and prices (the default)
 *   STRUCTURED_ITEMS  — itemised, descriptions only, NO prices
 *   SUMMARY_ONLY      — no item breakdown at all, total only
 *
 * Verified live 2026-08-24: all 271 work_orders are BILLABLE_ITEMS, so the other two
 * branches are correct-by-construction rather than exercised by data. Treat them as
 * unverified paths, not as proven behaviour.
 */

export const VISIBILITY_POLICIES = ['BILLABLE_ITEMS', 'STRUCTURED_ITEMS', 'SUMMARY_ONLY'];

/**
 * Pricing models whose price comes from a HEADER column, not from the line items.
 *
 * recalculate_work_order_financials reads flat_rate_amount / percentage ×
 * percentage_base_amount / unit_count × unit_price for these and IGNORES
 * work_order_line_items entirely. On such a quote the line items are the contractor's
 * INTERNAL cost build-up — real, wanted, and none of the customer's business. The
 * customer is being asked to pay one fixed price.
 *
 * Same list the customer-facing PDF and email have always used (QuotePDFService's
 * pricing-model switch, EmailPreviewService's TIME_MATERIALS-only item block).
 */
export const HEADER_PRICED_MODELS = ['FLAT_RATE', 'PERCENTAGE', 'UNIT', 'RECURRING', 'MILESTONE'];

/**
 * Was this FLAT_RATE quote BUILT FROM its line items?
 *
 * The industry-standard way to price a flat-rate job (Housecall Pro, ServiceTitan) is to
 * pick price-book items and let their sum BE the binding price. When a quote is built that
 * way its lines are the price, not an internal cost build-up — so they are billable and
 * the customer may see them, subject to visibility_policy.
 *
 * Defaults FALSE (migration 20260828q), which is the older behaviour: flat_rate_amount is
 * the price and the lines are the contractor's private cost workings. The two cannot be
 * told apart by inspection, which is why this is an explicit column and not a heuristic.
 */
export const pricesFromLineItems = (record) =>
  record?.prices_from_line_items === true
  && String(record?.pricing_model || '').toUpperCase() === 'FLAT_RATE';

/**
 * Is this row the SYSTEM row rather than something a human typed?
 *
 * QuotesDatabasePanel.upsertSystemPricingLineItem mirrors the header price into a
 * `<MODEL> Pricing Model` line so consumers that only know how to read line items can
 * still see the amount. It is marked twice — `line_item_type = 'system'` and
 * `pricing_context.system = true` — and both markers were present on all 18 such rows
 * live 2026-08-28.
 *
 * It must NEVER reach a customer: it renders as the literal string
 * "FLAT_RATE Pricing Model". It reached one on 2026-08-28 because the portal's quote
 * table filtered only `is_optional`.
 */
export const isInternalLineItem = (item) => {
  if (!item) return false;
  if (item.line_item_type === 'system') return true;
  try {
    const ctx = typeof item.pricing_context === 'string'
      ? JSON.parse(item.pricing_context)
      : item.pricing_context;
    return ctx?.system === true;
  } catch {
    return false;
  }
};

/** Drop rows the customer must never see. Use on ANY customer-facing item list. */
export const customerVisibleItems = (items = []) => items.filter(it => !isInternalLineItem(it));

/**
 * Pricing models the DB BILLS from a header column instead of from the line items.
 *
 * Deliberately NARROWER than HEADER_PRICED_MODELS: those five hide items from the
 * customer, but only these three have an explicit branch in
 * recalculate_work_order_financials that reads a header column
 * (flat_rate_amount / percentage × percentage_base_amount / unit_count × unit_price).
 * MILESTONE and RECURRING fall to the trigger's line-item ELSE branch and have their own
 * billing paths, so they must NOT be billed off the system row — measured live
 * 2026-08-28, MILESTONE work orders carry a $0.00 system row against a $1,500 total, so
 * billing it would invoice zero.
 */
export const HEADER_BILLED_MODELS = ['FLAT_RATE', 'PERCENTAGE', 'UNIT'];

/**
 * The line items an INVOICE may charge for, given the work order's pricing model.
 *
 * Mirrors recalculate_work_order_financials so the invoice total equals the work order
 * total. Before this existed the invoice copied BOTH the system row and the internal
 * build-up: a $185 flat-rate job invoiced at $370 (INV-2026-0001, 2026-08-28).
 *
 *   header-billed  -> ONLY the system row, which equals the header amount
 *   everything else -> ONLY real rows; the system row is a mirror and would double-count
 *
 * Unselected optional add-ons are excluded by the CALLER, which already does so.
 */
export const billableInvoiceItems = (items = [], pricingModel, workOrder = null) => {
  const model = String(pricingModel || 'TIME_MATERIALS').toUpperCase();
  // A FLAT_RATE quote built from its line items bills those lines — they ARE the price.
  const fromLines = workOrder
    ? pricesFromLineItems({ ...workOrder, pricing_model: workOrder.pricing_model ?? pricingModel })
    : false;
  return (HEADER_BILLED_MODELS.includes(model) && !fromLines)
    ? items.filter(it => isInternalLineItem(it))
    : items.filter(it => !isInternalLineItem(it));
};

/** Falls back to BILLABLE_ITEMS when unset, matching the existing portal behaviour. */
const normalizePolicy = (record) => {
  const raw = typeof record === 'string' ? record : record?.visibility_policy;
  const value = String(raw || '').toUpperCase();
  return VISIBILITY_POLICIES.includes(value) ? value : 'BILLABLE_ITEMS';
};

export const getVisibilityPolicy = normalizePolicy;

/**
 * May the customer see an item breakdown at all?
 *
 * TWO independent gates, and both must pass:
 *   1. visibility_policy is not SUMMARY_ONLY (the contractor's explicit choice), and
 *   2. the pricing model actually BILLS from line items.
 *
 * Gate 2 was missing until 2026-08-28. On a $400 FLAT_RATE quote the portal listed the
 * internal build-up — "FLAT_RATE Pricing Model $400", PVC fittings $40, Diagnostic $50,
 * City permit $25 — above a $400 total, so the customer saw $515 of parts and a price
 * that did not add up, plus a raw enum. The PDF and the email had this gate; the portal
 * never did, which is exactly the "second copy of the rule" this file was created to
 * prevent.
 */
export const showsLineItems = (record) =>
  normalizePolicy(record) !== 'SUMMARY_ONLY'
  && (!HEADER_PRICED_MODELS.includes(String(record?.pricing_model || '').toUpperCase())
      || pricesFromLineItems(record));

/**
 * May the customer see PER-LINE prices?
 *
 * Note this is deliberately stricter than showsLineItems: STRUCTURED_ITEMS lists what was
 * done without exposing how each line was priced. The order total is always shown — it is
 * what the customer is being asked to pay.
 */
export const showsLineItemPrices = (record) => normalizePolicy(record) === 'BILLABLE_ITEMS';
